import { memo, type CSSProperties } from 'react'
import { timelineSteps } from '../agenda/useAgendaLayout.js'

function clockLabel(minuteOfDay: number): string {
  const hours = Math.floor(minuteOfDay / 60) % 24
  return `${String(hours).padStart(2, '0')}:${String(Math.round(minuteOfDay % 60)).padStart(2, '0')}`
}

/**
 * Axe horaire de la semaine type, partagé par le modèle et sa prévisualisation.
 * Sans lui, la position verticale d'un créneau n'était lisible que dans son
 * libellé : on voyait la forme de la semaine sans savoir à quelle heure elle
 * commençait. La graduation se densifie au zoom selon la même progression que
 * l'agenda (`timelineSteps`) : à échelle égale, les deux grilles portent la
 * même trame et se lisent pareil.
 */
export const TemplateHourAxis = memo(function TemplateHourAxis({ hourHeight }: { hourHeight: number }) {
  const { labelEvery, tickEvery } = timelineSteps(hourHeight)
  const tickCount = Math.round((24 * 60) / tickEvery)
  return (
    <div className="template-axis" aria-hidden="true">
      <div className="template-day__header template-axis__header" />
      <div className="template-axis__body">
        {Array.from({ length: tickCount + 1 }, (_, index) => {
          const minuteOfDay = index * tickEvery
          const position = { '--template-axis-y': `${minuteOfDay / 60 * hourHeight}px` } as CSSProperties
          if (Number.isInteger(minuteOfDay) && minuteOfDay % labelEvery === 0) {
            const isHour = minuteOfDay % 60 === 0
            return (
              <div key={`label-${index}`} className={`template-axis__label${isHour ? ' is-hour' : ''}`} style={position}>
                {clockLabel(minuteOfDay)}
              </div>
            )
          }
          return <div key={`tick-${index}`} className="template-axis__tick" style={position} />
        })}
      </div>
    </div>
  )
})

/**
 * Variables de trame de la grille : hauteur d'heure et pas des filets mineurs.
 * Les deux vivent ensemble — un fond dont le pas ne suit pas l'échelle donne
 * une grille dont les filets ne tombent plus sur les graduations de l'axe.
 */
export function templateCanvasVars(hourHeight: number): CSSProperties {
  return {
    '--template-hour-height': `${hourHeight}px`,
    '--template-minor-height': `${timelineSteps(hourHeight).minorEvery / 60 * hourHeight}px`,
  } as CSSProperties
}
